<template>
  <form
    class="jps-search-box-simple"
    @submit.prevent="doSearch()"
    role="search"
  >
    <div class="search-keyword-field" v-if="hasKeyword">
      <input
        class="jps-input jps-keyword-input is-size-6-touch"
        :placeholder="placeholder"
        type="search"
        v-model="keyword"
        @keyup.prevent.esc="keyword = ''"
      />
    </div>
    <template v-if="fieldValues">
      <div
        class="search-keyword-field"
        v-for="sf in ss.state.searchdef.searchFields.filter(sf => !sf.hidden)"
      >
        <template v-if="isSearchFieldsOption(sf)">
          <template v-if="getSearchFieldsOption(sf).type === 'ComboBox'">
            <BaseComboBox
              v-model="fieldValues[sf.key]"
              :placeholder="sf.placeHolder"
              :options="getSearchFieldsOption(sf).option"
            />
          </template>
        </template>
        <template v-else>
          <input
            class="jps-input jps-keyword-input is-size-6-touch"
            :placeholder="ls(sf.placeHolder, '')"
            type="search"
            v-model="fieldValues[sf.key]"
            @keyup.prevent.esc="fieldValues[sf.key] = ''"
          />
        </template>
      </div>
    </template>
    <slot name="button">
      <button
        v-if="!auto"
        type="submit"
        class="jps-round-button jps-blue simple-search-button"
        :aria-label="ls(dapLangGlobal.search)"
      >
        <BaseIcon name="mdi/magnify" />
      </button>
    </slot>
  </form>
</template>

<script lang="ts" setup>
import { type CustomSearchField, dapLangGlobal } from '#imports'

const { ls } = useI18n()
const { loadingExecute } = useLoader()

interface Props {
  auto?: boolean
  loading?: boolean
  ss: SearchStore<any>
  ssOption?: CustomSearchFieldOption[]
}

const props = withDefaults(defineProps<Props>(), {
  auto: false,
  loading: false,
})

// キーワード検索
const keyword: Ref<string> = ref('')

const hasKeyword = computed(() => {
  return props.ss.state.searchdef.keywordFields?.length > 0
})

const placeholder = computed(() => {
  return ls(
    props.ss.state.searchdef.keywordPlaceholder ||
      dapLangGlobal.mDefaultPlaceholder,
  )
})

// オプション表示機能
const getSearchFieldsOption = (searchField: CustomSearchField) => {
  return props.ssOption?.find(option => option.key === searchField.key)
}

const isSearchFieldsOption = (searchField: CustomSearchField) => {
  return !!getSearchFieldsOption(searchField)
}

//その他のフィールド
const fieldValues = ref<{ [key: string]: string }>()

onBeforeMount(() => {
  if (props.ss.state.searchdef.searchFields) {
    const tmpFieldValues = {}
    props.ss.state.searchdef.searchFields.forEach((sf) => {
      tmpFieldValues[sf.key] = ''
    })
    fieldValues.value = tmpFieldValues
  }
  props.ss.addQueryListenr((ss) => {
    //キーワード
    if (props.ss.state.keyword) {
      keyword.value = joinKeywords(props.ss.state.keyword)
    } else {
      keyword.value = ''
    }
    //クエリ
    if (props.ss.state.query) {
      Object.keys(props.ss.state.query).forEach((key) => {
        if (fieldValues.value[key] != undefined) {
          fieldValues.value[key] = joinKeywords(props.ss.state.query[key])
        }
      })
    }
    //フィルタ
    if (props.ss.state.filter) {
      Object.keys(props.ss.state.filter).forEach((key) => {
        if (fieldValues.value[key] != undefined) {
          fieldValues.value[key] = joinKeywords(props.ss.state.filter[key])
        }
      })
    }
  })
  if (props.ss.state.keyword?.length > 0) {
    keyword.value = props.ss.state.keyword.join(' ')
  }
  // 画面を直接開いた時にURLからfieldValuesを復元する
  if (props.ss.state.query) {
    Object.keys(props.ss.state.query).forEach((key) => {
      if (fieldValues.value[key] != undefined) {
        fieldValues.value[key] = joinKeywords(props.ss.state.query[key])
      }
    })
  }
  if (props.auto) {
    watch(
      () => keyword,
      () => debouncedSearch(),
      { deep: true },
    )
    watch(
      () => fieldValues,
      () => debouncedSearch(),
      { deep: true },
    )
  }
})

const doSearch = () => {
  props.ss.state.from = 0
  if (hasKeyword.value) {
    props.ss.state.keyword = split(keyword.value)
  }
  if (props.ss.state.searchdef.searchFields) {
    props.ss.state.searchdef.searchFields.forEach(sf => {
      const value = fieldValues.value[sf.key]
      switch (sf.type) {
        case 'keyword':
          props.ss.state.query[sf.key] = split(value)
          break
        case 'code':
          props.ss.state.filter[sf.key] = split(value)
          break
      }
    })
  }
  if (props.loading) {
    loadingExecute(props.ss.execute())
  } else {
    props.ss.execute()
  }
}

const debouncedSearch = debounce(300, () => {
  doSearch()
})
</script>

<style lang="scss" scoped>
.jps-search-box-simple {
  display: flex;
  .search-keyword-field {
    flex-grow: 1;
  }
  .simple-search-button {
    width: 34px;
    padding: 0;
  }
  > *:not(:first-child) {
    margin-left: 0.5rem;
  }
}
</style>
